import { useContext, useEffect, useState } from 'react'

import { EtaDistanceResult, EtaLocation, EtaRouteStatus } from '@/feature/eta'
import { calculateEtaAndDistance } from '@/feature/eta/utils/calculateEtaAndDistance'
import { calculateZoomForBounds, MapContext } from '@/feature/map'
import { ObservatoryEtaSettings } from '@/feature/observatory'
import { useAuthStore } from '@/feature/auth'

import { ETA_FUDGE_FACTOR, formatDurationText, getErrorMessageForStatus } from '../utils/Eta.utils'

// City-level zoom used to frame the destination when a route can't be calculated, so the camera
// is never left at the wide initial from->to fit (which can read as a whole-world view).
const ETA_FALLBACK_ZOOM = 13

interface UseEtaCalculationProps {
  etaLocationFrom: EtaLocation
  etaLocationTo: EtaLocation
  applyFudgeFactor: boolean
  routeLayerConfig: ObservatoryEtaSettings
  organizationName: string | undefined
}

interface UseEtaCalculationResult {
  etaResult: EtaDistanceResult | undefined
  etaError: string | undefined
}

export const useEtaCalculation = ({
  etaLocationFrom,
  etaLocationTo,
  applyFudgeFactor,
  routeLayerConfig,
  organizationName,
}: UseEtaCalculationProps): UseEtaCalculationResult => {
  const [etaResult, setEtaResult] = useState<EtaDistanceResult | undefined>(undefined)
  const [etaError, setEtaError] = useState<string | undefined>(undefined)
  const map = useContext(MapContext)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  useEffect(() => {
    setEtaResult(undefined)
    setEtaError(undefined)

    if (!isAuthenticated || !etaLocationFrom || !etaLocationTo) return

    const fromValid = isFinite(etaLocationFrom.latitude) && isFinite(etaLocationFrom.longitude)
    const toValid = isFinite(etaLocationTo.latitude) && isFinite(etaLocationTo.longitude)

    // When there is no route to frame, recover by centering on the destination the user was
    // heading towards, rather than leaving the camera on the originally selected asset.
    const centerOnDestination = () => {
      if (map && toValid) {
        map.panTo({
          position: { lat: etaLocationTo.latitude, lng: etaLocationTo.longitude },
          zoom: ETA_FALLBACK_ZOOM,
        })
      }
    }

    // Frame the whole journey: fit the camera to the bounds spanning the asset (origin), the
    // geofence (destination), and the route path between them, so the user can see the full route.
    // The route's own viewport (`result.bounds`) frames too tightly around the destination.
    const frameRoute = (path: EtaDistanceResult['overviewPath']) => {
      if (!map || !fromValid || !toValid) {
        centerOnDestination()
        return
      }

      const bounds = new window.google.maps.LatLngBounds()
      bounds.extend({ lat: etaLocationFrom.latitude, lng: etaLocationFrom.longitude })
      bounds.extend({ lat: etaLocationTo.latitude, lng: etaLocationTo.longitude })
      path?.forEach((point) => bounds.extend(point))

      const targetZoom = calculateZoomForBounds(bounds, map.map.getDiv().getBoundingClientRect())
      map.panTo({
        position: bounds.getCenter(),
        zoom: targetZoom - 0.4,
      })
    }

    calculateEtaAndDistance(etaLocationFrom, etaLocationTo, routeLayerConfig, organizationName)
      .then((result) => {
        // The Routes API resolves successfully with an empty route (no duration/path/bounds) when
        // no route can be found — it does not reject. Treat that as a failure: surface the message
        // and recover the camera, rather than leaving the wide initial fit with a blank readout.
        if (result?.duration == null) {
          setEtaError(getErrorMessageForStatus(EtaRouteStatus.NoRouteFound))
          centerOnDestination()
          return
        }

        frameRoute(result.overviewPath)

        if (applyFudgeFactor && result.duration) {
          const fudgedSeconds = Math.round(result.duration.value * (1 + ETA_FUDGE_FACTOR))
          result.duration = {
            text: formatDurationText(fudgedSeconds),
            value: fudgedSeconds,
          }
        }

        setEtaResult(result)
      })
      .catch((error: Error) => {
        const status = error?.message || 'UNKNOWN_ERROR'
        setEtaError(getErrorMessageForStatus(status))
        centerOnDestination()
      })
  }, [etaLocationFrom, etaLocationTo, isAuthenticated])

  return { etaResult, etaError }
}
